import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import PaidOutlined from "@mui/icons-material/PaidOutlined";
import QRCode from "qrcode";
import DashboardClientLayout from "../../Layouts/DashboardClientLayout";
import BookingApi from "Services/Booking";

import qrcode from "../../assets/qrcode.png";

const panelStyle = {
  border: "1px solid #d8e2ee",
  borderRadius: 12,
  background: "#fff",
  boxShadow: "0 10px 24px rgba(8, 26, 52, 0.05)",
};

function formatBookingDate(value) {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatBookingTime(value) {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", hour12: false });
}

function getBookingNumber(booking) {
  return (
    booking?.booking_number ||
    booking?.bookingNumber ||
    booking?.reference ||
    booking?.reference_number ||
    booking?.id ||
    "--"
  );
}

function getRideTitle(ride) {
  const origin =
    ride?.places_rides_origin_place_idToplaces?.name ||
    ride?.origin?.name ||
    ride?.origin?.addressName ||
    ride?.origin_place_name ||
    ride?.originLabel ||
    "D?part";
  const destination =
    ride?.places_rides_destination_place_idToplaces?.name ||
    ride?.destination?.name ||
    ride?.destination?.addressName ||
    ride?.destination_place_name ||
    ride?.destinationLabel ||
    "Arriv?e";
  return `${origin} ? ${destination}`;
}

function getBookingId(booking) {
  return booking?.id || booking?.booking_id || booking?.bookingId || booking?.reference_id || booking?.referenceNumber || "";
}

function getBookingWaypointName(booking, direction) {
  const pickupWaypoint =
    booking?.ride_waypoints_bookings_pickup_waypoint_idToride_waypoints ||
    booking?.pickup_waypoint ||
    booking?.pickupWaypoint ||
    booking?.pickup_waypoint_details ||
    booking?.pickupWaypointDetails ||
    {};
  const dropoffWaypoint =
    booking?.ride_waypoints_bookings_dropoff_waypoint_idToride_waypoints ||
    booking?.dropoff_waypoint ||
    booking?.dropoffWaypoint ||
    booking?.dropoff_waypoint_details ||
    booking?.dropoffWaypointDetails ||
    {};

  const waypoint = direction === "pickup" ? pickupWaypoint : dropoffWaypoint;
  return (
    waypoint?.places?.name ||
    waypoint?.place?.name ||
    waypoint?.place_name ||
    waypoint?.name ||
    waypoint?.label ||
    "--"
  );
}

function getBookingWaypointAddress(booking, direction, fallback) {
  const pickupWaypoint =
    booking?.ride_waypoints_bookings_pickup_waypoint_idToride_waypoints ||
    booking?.pickup_waypoint ||
    booking?.pickupWaypoint ||
    booking?.pickup_waypoint_details ||
    booking?.pickupWaypointDetails ||
    {};
  const dropoffWaypoint =
    booking?.ride_waypoints_bookings_dropoff_waypoint_idToride_waypoints ||
    booking?.dropoff_waypoint ||
    booking?.dropoffWaypoint ||
    booking?.dropoff_waypoint_details ||
    booking?.dropoffWaypointDetails ||
    {};

  const waypoint = direction === "pickup" ? pickupWaypoint : dropoffWaypoint;
  return waypoint?.places?.addressName || waypoint?.addressName || waypoint?.address || fallback || "--";
}

function getRidePlace(ride, direction) {
  return direction === "pickup"
    ? ride?.places_rides_origin_place_idToplaces || ride?.origin_place || ride?.origin || ride?.originPlace
    : ride?.places_rides_destination_place_idToplaces || ride?.destination_place || ride?.destination || ride?.destinationPlace;
}

function getBookingPlaceLabel(booking, ride, direction) {
  const bookingWaypoint = getBookingWaypointName(booking, direction);
  if (bookingWaypoint && bookingWaypoint !== "--") {
    return bookingWaypoint;
  }

  const place = getRidePlace(ride, direction);
  return place?.name || place?.addressName || place?.place_name || place?.label || "--";
}

function getBookingPlaceAddress(booking, ride, direction, fallback) {
  const bookingAddress = getBookingWaypointAddress(booking, direction, fallback);
  if (bookingAddress && bookingAddress !== "--" && bookingAddress !== fallback) {
    return bookingAddress;
  }

  const place = getRidePlace(ride, direction);
  return place?.addressName || place?.address || place?.places?.addressName || fallback || "--";
}

async function buildQrDataUri(payload) {
  const text = JSON.stringify(payload);
  return QRCode.toDataURL(text, {
    errorCorrectionLevel: "M",
    margin: 1,
    width: 300,
    color: {
      dark: "#0b7a45",
      light: "#ffffff",
    },
  });
}

export default function BookingConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const bookingIdFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    console.log( params.get("bookingId") || "")
    return params.get("bookingId") || "";
  }, [location.search]);
  const storedPayload = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("bookingConfirmationPayload");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const [bookingState, setBookingState] = useState(location.state?.booking || storedPayload?.booking || null);
  const [rideState, setRideState] = useState(location.state?.ride || storedPayload?.ride || null);
  const [passengerCount, setPassengerCount] = useState(Number(location.state?.passengerCount || storedPayload?.passengerCount || 1));
  const booking = bookingState;
  const ride = rideState;
  const passengerEmail =
    location.state?.passengerEmail ||
    storedPayload?.passengerEmail ||
    booking?.user?.email ||
    booking?.email ||
    localStorage.getItem("email") ||
    "your email address";

  const bookingNumber = useMemo(() => getBookingNumber(booking), [booking]);
  const bookingId = useMemo(() => getBookingId(booking) || bookingIdFromQuery, [booking, bookingIdFromQuery]);
  const departureDate = formatBookingDate(ride?.departure_at);
  const departureTime = formatBookingTime(ride?.departure_at);
  const arrivalTime = formatBookingTime(ride?.estimated_arrival_at);
  const tripTitle = getRideTitle(ride);
  const pickupPointLabel = getBookingPlaceLabel(booking, ride, "pickup");
  const dropoffPointLabel = getBookingPlaceLabel(booking, ride, "dropoff");
  const totalAmount = booking?.total_amount || booking?.amount || "";
  const totalCurrency = booking?.currency || ride?.currency || "XAF";
  const [pdfLoading, setPdfLoading] = useState(false);
  const [qrDataUri, setQrDataUri] = useState("");
  const [qrLoading, setQrLoading] = useState(false);

  useEffect(() => {
    const bookingFromState = location.state?.booking || storedPayload?.booking || null;
    const rideFromState = location.state?.ride || storedPayload?.ride || null;

    setBookingState(bookingFromState);
    setRideState(rideFromState);
    setPassengerCount(Number(location.state?.passengerCount || storedPayload?.passengerCount || 1));
  }, [location.state, storedPayload]);

  useEffect(() => {
    let cancelled = false;

    const loadBooking = async () => {
      if (!bookingIdFromQuery || bookingState) {
        return;
      }

      try {
        const response = await BookingApi.getBookingById(bookingIdFromQuery);
        const data = response?.data?.data || response?.data || null;
        if (cancelled || !data) {
          return;
        }

        setBookingState(data);
        setRideState(data?.ride || data?.rides || data?.rides?.[0] || null);
        setPassengerCount(Number(data?.seats_booked || data?.passengerCount || 1));
      } catch {
        if (!cancelled) {
          setBookingState(null);
        }
      }
    };

    void loadBooking();

    return () => {
      cancelled = true;
    };
  }, [bookingIdFromQuery, bookingState]);

  useEffect(() => {
    let cancelled = false;

    const loadQr = async () => {
      if (!bookingId) {
        setQrDataUri("");
        return;
      }

      setQrLoading(true);
      try {
        const tenantId =
          localStorage.getItem("tenantId") ||
          localStorage.getItem("tenant_id") ||
          localStorage.getItem("tenant-slug") ||
          "default";
        const passenger = booking?.passenger || booking?.user || booking?.passengerDetails || null;
        const driver = ride?.driver || ride?.users || ride?.driverDetails || null;
        const qrOrigin = getBookingPlaceLabel(booking, ride, "pickup");
        const qrDestination = getBookingPlaceLabel(booking, ride, "dropoff");
        const bookingConfirmationUrl = "http://localhost:3002/booking-confirmation";

        const dataUri = await buildQrDataUri({
          url: bookingConfirmationUrl,
        });

        if (!cancelled) {
          setQrDataUri(dataUri);
        }
      } catch {
        if (!cancelled) {
          setQrDataUri("");
        }
      } finally {
        if (!cancelled) {
          setQrLoading(false);
        }
      }
    };

    void loadQr();

    return () => {
      cancelled = true;
    };
  }, [booking, bookingId, passengerCount, ride]);

  const clearAndGoHome = () => {
    sessionStorage.removeItem("bookingConfirmationPayload");
    navigate("/dashboard-client");
  };

  const handleDownloadPdfTicket = async () => {
    if (!bookingId) {
      return;
    }

    setPdfLoading(true);
    try {
      const response = await BookingApi.downloadBookingPdf(bookingId);
      const blob = new Blob([response.data], { type: response.headers?.["content-type"] || "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `booking-${bookingNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setPdfLoading(false);
    }
  };

  

  return (
    <>
      <Box sx={{ minHeight: "100vh", background: "#f4f7fb", p: { xs: 1.5, md: 2.5 } }}>
        <Box sx={{ maxWidth: 1180, mx: "auto", display: "grid", gap: 1.75 }}>
        <div
          style={{
            ...panelStyle,
            padding: "18px 20px",
            display: "grid",
            gap: 14,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <h1 style={{ margin: 0, fontSize: 30, lineHeight: 1.1, fontWeight: 900, color: "#10233f" }}>
              Réservation confirmée!
            </h1>
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: "999px",
                background: "#1a8f12",
                color: "#fff",
                display: "inline-grid",
                placeItems: "center",
                fontWeight: 900,
                fontSize: 18,
              }}
            >
              ✓
            </span>
          </div>

          <div style={{ color: "#5f6e81", fontWeight: 600 }}>Numéro de réservation: {bookingNumber}</div>

          <div style={{ display: "flex", gap: 16, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ color: "#10233f", fontWeight: 700, lineHeight: 1.4 }}>
                Merci d'avoir choisi de voyager avec nous.
              </div>
              <div style={{ color: "#10233f", fontWeight: 800 }}>
                Nous avons envoyé un e-mail à: <span style={{ color: "#10233f" }}>{passengerEmail}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleDownloadPdfTicket}
              disabled={pdfLoading || !bookingId}
              style={{
                border: "none",
                background: "#0b7a45",
                color: "#fff",
                minWidth: 210,
                height: 42,
                borderRadius: 5,
                fontWeight: 900,
                cursor: "pointer",
                opacity: pdfLoading || !bookingId ? 0.7 : 1,
                transition: "background 0.2s ease",
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.background = "#09663a";
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.background = "#0b7a45";
              }}
            >
              {pdfLoading ? "Téléchargement..." : "Télécharger votre Ticket"}
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(0, 1fr))", gap: 12 }}>
          <div style={{ ...panelStyle, gridColumn: "span 7", padding: 18 }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#154b66", textAlign: "center", marginBottom: 18 }}>
              Aperçu de votre réservation
            </div>

            <div style={{ color: "#154b66", fontWeight: 800, marginBottom: 16 }}>{departureDate}</div>

            <div style={{ display: "grid", gap: 18 }}>
              <div style={{ display: "grid", gridTemplateColumns: "90px 1fr", gap: 14, alignItems: "start" }}>
                <div style={{ fontWeight: 900, color: "#10233f" }}>{departureTime}</div>
                <div style={{ display: "grid", gap: 4 }}>
                  <div style={{ fontWeight: 800, color: "#10233f" }}>{pickupPointLabel}</div>
                  <div style={{ color: "#64748b" }}>{getBookingPlaceAddress(booking, ride, "pickup", ride?.origin?.name || ride?.originLabel || "Départ")}</div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "90px 1fr", gap: 14, alignItems: "start" }}>
                <div style={{ fontWeight: 900, color: "#10233f" }}>{arrivalTime}</div>
                <div style={{ display: "grid", gap: 4 }}>
                  <div style={{ fontWeight: 800, color: "#10233f" }}>{dropoffPointLabel}</div>
                  <div style={{ color: "#64748b" }}>{getBookingPlaceAddress(booking, ride, "dropoff", ride?.destination?.name || ride?.destinationLabel || "Arrivée")}</div>
                </div>
              </div>
            </div>

            <div style={{ borderTop: "1px solid #d8e2ee", marginTop: 18, paddingTop: 18, color: "#154b66", fontWeight: 800 }}>
            </div>
            <div
              style={{
                marginTop: 10,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                borderRadius: 12,
                background: "linear-gradient(180deg, #fbe4c8 0%, #f7d1a6 100%)",
                color: "#ef6c00",
                fontWeight: 900,
                width: "fit-content",
                maxWidth: "100%",
                boxShadow: "0 8px 18px rgba(239, 108, 0, 0.08)",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: "999px",
                  border: "2px solid #ef6c00",
                  display: "inline-grid",
                  placeItems: "center",
                  flexShrink: 0,
                  fontSize: 10,
                  lineHeight: 1,
                }}
              >
                <PaidOutlined sx={{ fontSize: 12, color: "#ef6c00" }} />
              </span>
              <span>
                {passengerCount} Siège(s) réservé(s){passengerCount > 1 ? "s" : ""} -- {totalAmount ? `Frais total payé ${totalAmount} ${totalCurrency}` : ""}
              </span>
            </div>
          </div>

          <div style={{ ...panelStyle, gridColumn: "span 5", padding: 18, display: "grid", gap: 14 }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#154b66", textAlign: "center" }}>Télécharger votre ticket</div>
         

            <div style={{ height: 170, borderRadius: 16, background: "linear-gradient(180deg, #eef5fb, #ffffff)", border: "1px dashed #b7c9da", padding: 12, align : "center" }}>
              {/* {qrLoading && !qrDataUri ? (
                <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: "#64748b", fontWeight: 700 }}>
                  Génération du QR...
                </div>
              ) : qrDataUri ? ( */}
                
                <img
                  style={{ width: "150px", height: "150px", display: "block", margin: "0 auto" }}
                src={qrcode}
                  alt="QR code de réservation"
                />
              {/* ) : (
                <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: "#64748b", fontWeight: 700, textAlign: "center", padding: 12 }}>
                  Impossible de générer le QR pour le moment.
                </div>
              )} */}
            </div>

            <div style={{ color: "#64748b", fontSize: 13, textAlign: "center" }}>
              Scannez le code QR ci-dessus avec votre téléphone pour télécharger le ticket de réservation.
            </div>
          </div> 
        </div>

        <div style={{ ...panelStyle, padding: 18, display: "grid", gridTemplateColumns: "1fr auto", gap: 16, alignItems: "center" }}>
          <div>
            <div style={{ color: "#154b66", fontWeight: 900, fontSize: 18, marginBottom: 8 }}>
              Vous souhaitez modifier ou améliorer votre voyage ?
            </div>
            <div style={{ color: "#37475f", lineHeight: 1.6 }}>
              Utilisez notre espace en libre-service « Gérer ma réservation » pour ajouter des options supplémentaires, modifier l'horaire de votre réservation ou modifier d'autres détails de votre voyage.
            </div>
          </div>
          <button
            type="button" 
            // onClick={#}
            style={{
              minWidth: 300,
              height: 44,
              borderRadius: 6,
              border: "1px solid #c9d5e2",
              background: "#fff",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Gérer ma réservation
          </button>
        </div>

        <div style={{ ...panelStyle, padding: 18, display: "grid", gridTemplateColumns: "1fr auto", gap: 16, alignItems: "center" }}>
          <div>
            <div style={{ color: "#154b66", fontWeight: 900, fontSize: 18, marginBottom: 8 }}>
              Consulter ou gérer ma facture
            </div>
            <div style={{ color: "#37475f", lineHeight: 1.6 }}>
              Pour consulter ou gérer vos factures, rendez-vous sur notre portail de facturation.
            </div>
          </div>
          <button
            type="button"
            // onClick='#'
            style={{
              minWidth: 300,
              height: 44,
              borderRadius: 6,
              border: "1px solid #c9d5e2",
              background: "#fff",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Télécharger votre facture
          </button>
        </div>
        </Box>
      </Box>
    </>
  );
}
